/*
 * Project: OKIT.VCM
 * 
 * Copyright 2023 by OKIT GmbH
 * All rights reserved.
 * 
 * Diese Software ist urheberrechtlich geschützt.
 */
import React, { useEffect, useState } from "react";
import { MenuItem, InputBase, Menu, Divider } from "@material-ui/core"
import CheckIcon from "@material-ui/icons/Check";
import { Button } from "reactstrap";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import i18next from 'i18next';
import auth from "../auth/auth";
import ROLES from "../utils/roles";
import useStyles from "../assets/css/clientPrefixStyles";
import { clientPrefixApi, setSessionApi } from "../hooks/clientPrefixApi";
import { getSessionClientPrefixApi } from "../hooks/sessionClientPrefixApi";
import TimeOutToast from "../utils/TimeOutToast";


/**
 * This component represents the the DropDown
 * List to choose the clientPrefix
 * 
 * @author hatem sfar
 *
 */
const ClientPrefixDropDown = () => {


  const storedClientPrefix = localStorage.getItem("clientPrefix");
  const clientPrefix =
    storedClientPrefix && storedClientPrefix !== "null"
      ? storedClientPrefix
      : null; // if localStorage empty must have the session from server
  const classes = useStyles();
  const [data, setData] = useState([])
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selection, setSelection] = useState(clientPrefix || "");
  const [hasLoaded, setHasLoaded] = useState(false);
  const isMenuOpen = Boolean(anchorEl); // Track menu open state

  // Cross-render cache to avoid calling `/api/device/uniquedevices` every time
  // the dropdown is opened (or after a remount/refresh).
  const CLIENT_PREFIX_LIST_CACHE_KEY = "clientPrefixListCache";
  const CLIENT_PREFIX_LIST_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes



  //eslint-disable-next-line



  // Selection is hydrated from backend session in the prefetch effect below.


  // Prefetch clientPrefix list on app launch (when dropdown mounts).
  // We read `clientPrefix` from backend session (single-flight) so the button name
  // appears without waiting for a user click.
  useEffect(() => {
    if (hasLoaded) return;

    let cancelled = false;

    const hydrate = async () => {
      const maxAttempts = 6;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const prefix = await getSessionClientPrefixApi();
        if (!prefix) {
          await new Promise((r) => setTimeout(r, 350));
          continue;
        }
        if (cancelled) return;

        localStorage.setItem("clientPrefix", prefix);
        setSelection(prefix);

        // Hydrate from cache if still fresh.
        try {
          const raw = localStorage.getItem(CLIENT_PREFIX_LIST_CACHE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            const atMs = parsed?.atMs ? Number(parsed.atMs) : 0;
            if (
              parsed?.value &&
              atMs &&
              Date.now() - atMs < CLIENT_PREFIX_LIST_CACHE_TTL_MS
            ) {
              setData(parsed.value);
              setHasLoaded(true);
              return;
            }
          }
        } catch (e) {
          // ignore cache errors
        }

        // Cache missing/expired -> fetch from backend and store in cache.
        const prefixes = await getClientPrefix();
        if (!Array.isArray(prefixes)) {
          await new Promise((r) => setTimeout(r, 350));
          continue;
        }
        if (cancelled) return;
        setHasLoaded(true);
        return;
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoaded]);

  const handleMenuToggle = (event) => {
    const nextOpen = !isMenuOpen;
    setAnchorEl(nextOpen ? event.currentTarget : null);
  };

  const handleClose = (e) => {
    if (e.target.innerText !== selection && e.target.innerText !== "") {
      setSelection(e.target.innerText);
      localStorage.setItem("clientPrefix", e.target.innerText);
      updateSessionClientPrefix(localStorage.getItem("clientPrefix"))
      localStorage.removeItem("zoomLevel");
      localStorage.removeItem("position");
    }
    setSearchText("");
    setAnchorEl(null);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const getClientPrefix = async (req, res) => {

    if (auth([ROLES.OPERATOR])) {
      // Try localStorage first to avoid backend call on dropdown open.
      try {
        const raw = localStorage.getItem(CLIENT_PREFIX_LIST_CACHE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.value && parsed?.atMs && Date.now() - parsed.atMs < CLIENT_PREFIX_LIST_CACHE_TTL_MS) {
            setData(parsed.value);
            return parsed.value;
          }
        }
      } catch (e) {
        // Ignore cache errors and fall back to server fetch.
      }

      try {
        const data = await clientPrefixApi();
        if (!Array.isArray(data)) {
          return null;
        }
        const next = data ? data : [];
        setData(next);
        try {
          localStorage.setItem(
            CLIENT_PREFIX_LIST_CACHE_KEY,
            JSON.stringify({ value: next, atMs: Date.now() })
          );
        } catch (e) {
          // Ignore localStorage quota errors.
        }
        return next;
      } catch (err) {
        if (err.response && err.response.status === 504) {
          console.error("Gateway Time-out: The server took too long to respond.");
          TimeOutToast()
        }
        console.log(err);
      }

    } else {
      return null;
    }

  };

  // Session/clientPrefix is handled centrally during app bootstrap.
  // Keep this helper as a no-op to avoid calling /api/session/session repeatedly.
  const getSessionClientPrefix = async () => null;

  const updateSessionClientPrefix = async (client) => {
    if (auth([ROLES.OPERATOR])) {
      setSessionApi(client)
        .then(() => {
          setSelection(client)
          localStorage.removeItem('zoomLevelLat');
          localStorage.removeItem('zoomLevelLong');
          window.location.replace("/devices")
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      return null;
      // res.status(401).json({ message: "You don't have permission" });
    }
  };



  return (
    <>
    
      <div style={{ textAlign: "-webkit-center" }}>
        <Button
          type="button"
          className={classes.DropDownButton}
          onClick={handleMenuToggle} // Toggle menu open/close
          style={{
            height: window.innerWidth < 991 && "40px",
            width: "max-content"
          }}
        >
          {selection}
          <KeyboardArrowDownIcon />
        </Button>
        {renderDashboardMenu()}
      </div>
    </>
  );


  function renderDashboardMenu() {
    const displayOptions =
      data?.map && data.map((item) => {
        if (item.toLowerCase().includes(searchText.toLowerCase())) {
          return item;
        }
        return undefined;
      })
        .filter((item) => item !== undefined);

    function renderOption(value) {
      if (selection === value) {
        return (
          <div className={classes.checkedItem}>
            <CheckIcon />
            {value}
          </div>
        );
      }
      return value;
    }

    const menuItems = [
      <MenuItem
        key="searchBarContainer"
        className={classes.searchBarContainer}
        disableTouchRipple={true}
      >
        <div className={classes.search}>
          <div className={classes.searchIcon}>
            <SearchIcon />
          </div>
          <InputBase
            name="searshbar"
            placeholder={`${i18next.t('Search')}...`}
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput
            }}
            onChange={handleSearchChange}
            value={searchText}
          />
        </div>
      </MenuItem>,
      <Divider key="searchDivider" />,
      ...displayOptions?.map((item, index) => {
        return (
          <div key={index}>
            <MenuItem onClick={(e) => handleClose(e)}>
              {renderOption(item)}
            </MenuItem>
            <Divider className={classes.menuDivider} />
          </div>
        );
      })
    ];

    if (displayOptions && !displayOptions?.includes(searchText) && searchText !== "") {
      menuItems.push(
        <div key="addOption">
          <MenuItem onClick={() => handleAddOption(searchText)}>
            {`Add "${searchText}"`}
          </MenuItem>
          <Divider className={classes.menuDivider} />
        </div>
      );
    }

    return (
      <Menu
        anchorEl={anchorEl}
        keepMounted={true}
        open={!!anchorEl}
        onClose={handleClose}
        className={classes.dashboardSelectMenu}
        anchorReference={null}
        anchorPosition={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
      >
        {menuItems}
      </Menu>
    );
  }

  function handleAddOption(newOption) {
    const options = [...data];
    options.push(newOption);
    setData(options);
    setSelection(newOption);
    localStorage.setItem("clientPrefix", newOption);
    updateSessionClientPrefix(newOption)
    setSearchText("");
    setAnchorEl(null);
    window.location.reload();
  }
};
export default ClientPrefixDropDown;